import type { Mechanism } from '../types';

// Multiple candidate paths per type: handles CFN naming mismatches (e.g. EventBridge vs Events) and nested shapes (e.g. OpenSearch DomainStatus.AccessPolicies).
const OPENSEARCH_PATHS: string[][] = [
  ['DomainStatus', 'AccessPolicies'],
  ['AccessPolicies'],
];

const POLICY_PATHS_BY_ASSET_TYPE: Record<string, string[][]> = {
  'AWS::S3::Bucket': [['Policy']],
  'AWS::IAM::Role': [['AssumeRolePolicyDocument']],
  'AWS::KMS::Key': [['Policy']],
  'AWS::SQS::Queue': [['Attributes', 'Policy']],
  'AWS::SNS::Topic': [['Attributes', 'Policy']],
  'AWS::Lambda::Function': [['Policy']],
  'AWS::SecretsManager::Secret': [['ResourcePolicy']],
  'AWS::OpenSearch::Domain': OPENSEARCH_PATHS,
  'AWS::OpenSearchService::Domain': OPENSEARCH_PATHS,
  'AWS::Elasticsearch::Domain': OPENSEARCH_PATHS,
  'AWS::EventBridge::EventBus': [['Policy']],
  'AWS::Events::EventBus': [['Policy']],
  'AWS::Schemas::Registry': [['Policy']],
  'AWS::EventSchemas::Registry': [['Policy']],
  'AWS::SES::EmailIdentity': [['Policies']],
  'AWS::ServerlessApplicationRepository::Application': [['Statements']],
  'AWS::DynamoDB::Table': [['Policy']],
  'AWS::EFS::FileSystem': [['Policy']],
  'AWS::ECR::Repository': [['policyText']],
  'AWS::Kinesis::Stream': [['Policy']],
  'AWS::Lambda::Layer': [['Policy']],
  // The web function collector stores the parsed resource policy at `policy`.
  'AWS::Lambda::WebFunction': [['policy']],
  'AWS::SageMaker::ModelPackageGroup': [['Policy']],
  // MI-162 cheap-win batch. Keys match the raw collected field name verbatim:
  // API Gateway returns lowercase `policy`; Backup uses AWS-standard `Policy`.
  'AWS::APIGateway::RestAPI': [['policy']],
  'AWS::Backup::BackupVault': [['Policy']],
  'AWS::CodeBuild::Project': [['Policy']],
  'AWS::CodeArtifact::Domain': [['Policy']],
  // MI-162 Step-C batch.
  'AWS::S3::AccessPoint': [['Policy']],
  'AWS::S3Express::AccessPoint': [['Policy']],
  'AWS::S3Express::DirectoryBucket': [['Policy']],
  'AWS::S3Tables::TableBucket': [['Policy']],
  'AWS::S3Tables::Table': [['Policy']],
  'AWS::S3Vectors::VectorBucket': [['Policy']],
  'AWS::Logs::Destination': [['Policy']],
  'AWS::Logs::DeliveryDestination': [['Policy']],
  'AWS::Oam::Sink': [['Policy']],
  'AWS::MediaTailor::Channel': [['Policy']],
  'AWS::VpcLattice::Service': [['Policy']],
  'AWS::VpcLattice::ServiceNetwork': [['Policy']],
  'AWS::APS::Workspace': [['Policy']],
  'AWS::CodeArtifact::Repository': [['Policy']],
  'AWS::Omics::SequenceStore': [['Policy']],
  'AWS::RUM::AppMonitor': [['Policy']],
  'AWS::Signer::SigningProfile': [['Policy']],
  // MI-162 central/regional policy-as-asset batch.
  'AWS::ECR::Registry': [['Policy']],
  'AWS::Glue::DataCatalog': [['Policy']],
  'AWS::XRay::ResourcePolicy': [['PolicyDocument']],
  'AWS::CloudWatchLogs::ResourcePolicy': [['policyDocument']],
  // MI-162 resource-policy gap (CloudTrail, WAFv2).
  'AWS::CloudTrail::Channel': [['Policy']],
  'AWS::CloudTrail::EventDataStore': [['Policy']],
  'AWS::WAFv2::RuleGroup': [['Policy']],
  // MI-162 gap-batch-2 resource-policy types. New collectors store the policy
  // JSON string at top-level `Policy`; BedrockAgentCore reuses its existing
  // `resourcePolicy` field.
  'AWS::WAF::RuleGroup': [['Policy']],
  'AWS::WAFRegional::RuleGroup': [['Policy']],
  'AWS::S3::AccessGrantsInstance': [['Policy']],
  'AWS::S3::MultiRegionAccessPoint': [['Policy']],
  'AWS::S3::FileSystem': [['Policy']],
  'AWS::Redshift::Namespace': [['Policy']],
  'AWS::RedshiftServerless::Snapshot': [['Policy']],
  'AWS::Bedrock::KnowledgeBase': [['Policy']],
  'AWS::SSM::Parameter': [['Policy']],
  'AWS::BedrockAgentCore::Gateway': [['resourcePolicy']],
  // MI-162 VPC Lattice resource configuration.
  'AWS::VpcLattice::ResourceConfiguration': [['Policy']],
  // MI-162 BedrockAgentCore runtime + endpoint.
  'AWS::BedrockAgentCore::Runtime': [['resourcePolicy']],
  'AWS::BedrockAgentCore::RuntimeEndpoint': [['resourcePolicy']],
  // MI-162 MSK cluster policy.
  'AWS::MSK::Cluster': [['Policy']],
  // MI-162 ImageBuilder.
  'AWS::ImageBuilder::Component': [['Policy']],
  'AWS::ImageBuilder::ContainerRecipe': [['Policy']],
  'AWS::ImageBuilder::ImageRecipe': [['Policy']],
};

// Mechanisms whose grant is described by a single policy document we can
// fetch from the resource. Everything else (ram_share, kms_grant,
// snapshot_share, ses_authz, cognito_user_pool) is configured via a
// different API surface and has no policy doc to show.
const POLICY_MECHANISMS = new Set<Mechanism>([
  'resource_policy',
  'trust_policy',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readPath(data: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = data;
  for (const segment of path) {
    if (!isObject(current)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function parseIfString(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    // Some AWS API responses ship policy docs URL-encoded
    // (e.g. AssumeRolePolicyDocument from older boto serializations).
    try {
      return JSON.parse(decodeURIComponent(value));
    } catch {
      return value;
    }
  }
}

export function mechanismCarriesPolicyDocument(mechanism: Mechanism): boolean {
  return POLICY_MECHANISMS.has(mechanism);
}

// Some AWS APIs return a map of { policyName: jsonString } rather than a bare
// policy object (e.g. SES EmailIdentity Policies). Parse each string value so
// the UI renders actual JSON rather than escaped strings.
function parseStringValues(value: unknown): unknown {
  if (!isObject(value) || Array.isArray(value)) {
    return value;
  }
  try {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        k,
        typeof v === 'string' ? parseIfString(v) : v,
      ]),
    );
  } catch {
    return value;
  }
}

export function extractResourcePolicy(
  assetType: string,
  rawData: Record<string, unknown> | undefined,
): unknown {
  const paths = POLICY_PATHS_BY_ASSET_TYPE[assetType];
  if (!paths || !rawData) {
    return null;
  }
  for (const path of paths) {
    const raw = readPath(rawData, path);
    if (raw !== undefined && raw !== null && raw !== '') {
      return parseStringValues(parseIfString(raw));
    }
  }
  return null;
}
